// A homeowner profile returned by a search (matched by email or phone).
import { Injectable } from "@angular/core";
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

/**
 * The view model the search screen renders: the homeowner `id` (the OTP target)
 * and the `email`. Mapped from the `Homeowner` the API returns.
 */
export interface HomeownerProfile {
    id: number;
    email: string;
}

export interface ProfilePhone {
    type: string;
    countryCallingCode: string;
    value: string;
}

/**
 * The editable profile fields sent when creating the homeowner's Property Pay
 * profile. `email` is inherited from SSO (read-only) but included in the payload here.
 */
export interface SsoProfileRequest {
    userId: number;
    firstName: string;
    lastName: string;
    phone: ProfilePhone;
    email: string;
}

/**
 * The result of validating a one-time passcode. `validated` is false for a wrong OR expired code.
 */
export interface ChallengeValidation {
    authenticated: boolean;
}

/**
 * The homeowner returned by the API search.
 */
export interface Homeowner {
    id: number;
    firstName?: string;
    lastName?: string;
    contactInformation?: {
        email?: { value: string },
        phones?: { value: string; type?: string }[];
    };
}

type BooleanResponse = { response: boolean };

@Injectable({ providedIn: "root" })
export class SsoHomeownerApiService extends HOAApiBase {
    constructor() {
        super("SsoHomeownerApiService");
    }

    /**
     *
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @param criteria is an object with either `email` or `phone`
     * @returns an observable of an array of homeowner profiles
     */
    search(
        userId: string,
        criteria: { email?: string; phone?: string }
    ): Observable<HomeownerProfile[]> {
        const params: Record<string, string> = criteria.email
            ? { email: criteria.email }
            : { phone: criteria.phone ?? "" };
        return this.http
            .get<Homeowner[]>(
                this.createUrl(
                    "hoa-gateway",
                    `/sso/homeowners/${userId}/search`
                ),
                { params }
            )
            .pipe(
                map(homeowners =>
                    homeowners.map(h => ({
                        id: h.id,
                        email: h.contactInformation?.email?.value ?? "",
                    }))
                )
            );
    }

    /**
     * Create/update the homeowner's Property Pay profile.
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @param profile is an object with `firstName`, `lastName`, `phone`, and `email`
     * @returns an observable of a boolean indicating whether the profile was created
     */
    updateProfile(
        profile: SsoProfileRequest
    ): Observable<void> {
        return this.http.post<void>(
            this.createUrl(
                "hoa-gateway",
                `/sso/homeowners/updateProfile`
            ),
            profile
        );
    }

    /**
     * Send (or re-send) a one-time passcode. The backend resolves the id
     * to that profile's email and mails the code (id is a path param).
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @param id is the profile id (email)
     * @returns an observable of a boolean indicating whether the code was sent
     */
    sendChallenge(userId: string, id: number): Observable<void> {
        return this.http.post<void>(
            this.createUrl(
                "hoa-gateway",
                `/sso/homeowners/${userId}/challenge/${id}`
            ),
            {}
        );
    }

    /**
     * Validate the entered passcode against the selected profile id.
     * Success is a 200; a wrong/expired code is a 4xx
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @param id is the profile id (email)
     * @param otp is the one-time passcode
     * @returns an observable of a boolean indicating whether the code was validated
     */
    validateChallenge(
        userId: string,
        id: number,
        otp: string
    ): Observable<ChallengeValidation> {
        return this.http
            .post(
                this.createUrl(
                    "hoa-gateway",
                    `/sso/homeowners/${userId}/challenge/validate`
                ),
                { id, otp },
                {
                    observe: "response",
                    responseType: "text",
                }
            )
            .pipe(
                map(res => ({ authenticated: res.status === 200 })),
                catchError(() => of({ authenticated: false }))
            );
    }

    /**
     * Once the OTP is authenticated, merge the SSO user into the existing profile.
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @param existingUserId is the existing profile id (hoahomeowner)
     * @returns an observable of the merged profile
     */
    mergeSsoHomeowner(
        userId: string,
        existingUserId: number
    ): Observable<Homeowner> {
        return this.http.post<Homeowner>(
            this.createUrl(
                "hoa-gateway",
                `/sso/homeowners/${userId}/merge/${existingUserId}`
            ),
            {}
        );
    }

    /**
     * Has this homeowner registered a Property Pay profile yet? The OAuth callback
     * calls this for vendor-SSO arrivals: false -> the create-or-find flow
     * (/sso/make-decision); true -> the registered app.
     * @param userId is always the arriving SSO user id (hoahomeowner)
     * @returns an observable of a boolean indicating whether the user is registered
     */

    isRegistered(userId: string): Observable<boolean> {
        return this.http
            .get<BooleanResponse>(
                this.createUrl(
                    "hoa-gateway",
                    `/sso/homeowners/${userId}/isRegistered`
                )
            )
            .pipe(map(body => body.response));
    }
}
